import classNames from 'classnames';
import React, { useContext } from 'react';

import { RicosViewerContext } from './../../ricos-context/viewer/index.js';
import type { Node } from './../../ricos-schema/legacy/wix/rich_content/v1/index.js';
import { Decoration_Type, Node_Type } from './../../ricos-schema/legacy/wix/rich_content/v1/index.js';
import type { LinkDecoration } from './../../ricos-schema/next-schema/index.js';
import type { NodeViewDecorator, ViewerContextualData } from './../../ricos-types/index.js';
import { getLinkHrefUrl } from './../../_shared/link/get-link-href-url.js';

import { WEB_LINK } from '../data-hooks.js';
import styles from '../statics/link-viewer-scss.js';
import { LINK_TYPE } from '../types.js';

const decorator = (
  element: JSX.Element,
  linkDecoration: LinkDecoration,
  contextualData: ViewerContextualData,
  node: Node
) => {
  const { baseUrl } = useContext(RicosViewerContext);
  const link = linkDecoration.linkData?.link;
  const target = link?.target || contextualData.anchorTarget;
  const rel = link?.rel || {};
  const relValue = [
    'noopener',
    rel.nofollow ? 'nofollow' : null,
    rel.sponsored ? 'sponsored' : null,
    rel.ugc ? 'ugc' : null,
    rel.noreferrer ? 'noreferrer' : null,
  ]
    .filter(Boolean)
    .join(' ');

  const onClick = () =>
    contextualData.helpers.onViewerAction?.(LINK_TYPE, 'Click', '', node.id, {
      type: Node_Type.TEXT,
      textData: node.textData,
    });

  return (
    <a
      key={`link=${element.key}`}
      target={`_${target?.toLowerCase?.()}`}
      href={link ? getLinkHrefUrl(link, baseUrl) : undefined}
      rel={relValue}
      className={classNames(styles.link, styles.linkInViewer)}
      data-hook={WEB_LINK}
      onClick={onClick}
    >
      {element}
    </a>
  );
};

const LinkDecorator: NodeViewDecorator<JSX.Element> = {
  rule: (node: Node) =>
    !!node.textData?.decorations?.some(({ type }) => type === Decoration_Type.LINK),
  decorate: ({ element, node, context }) => {
    const linkDecoration = node.textData?.decorations?.find(
      ({ type }) => type === Decoration_Type.LINK
    ) as LinkDecoration;
    return decorator(element as JSX.Element, linkDecoration, context, node);
  },
  priority: 1000,
};

export default LinkDecorator;
